<script setup lang="ts">
	import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
	import { X } from 'lucide-vue-next'
	import { Motion, AnimatePresence } from 'motion-v'
	import {
		MODAL_ANIMATIONS,
		resolveAnimationState,
		type ModalAnimationName,
	} from '@/components/common/mainModalAnimations'
	import { registerModal, unregisterModal } from '@/libs/modals'

	defineOptions({ inheritAttrs: false })

	type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
	type OverlayStyle = 'blur' | 'dots'
	type DrawerSide = 'left' | 'right'

	const props = withDefaults(
		defineProps<{
			modelValue?: boolean
			modalId?: string
			size?: ModalSize
			closable?: boolean
			closeOnOverlay?: boolean
			clickToClose?: boolean
			closeOnEsc?: boolean
			escToClose?: boolean
			lockScroll?: boolean
			showClose?: boolean
			title?: string
			customClass?: string
			overlayStyle?: OverlayStyle
			animation?: ModalAnimationName
			drawer?: DrawerSide
			bare?: boolean
		}>(),
		{
			modelValue: undefined,
			modalId: undefined,
			size: 'md',
			closable: true,
			closeOnOverlay: true,
			clickToClose: undefined,
			closeOnEsc: true,
			escToClose: undefined,
			lockScroll: true,
			showClose: true,
			customClass: '',
			overlayStyle: 'blur',
			animation: 'default',
			drawer: undefined,
			bare: false,
		},
	)

	const emit = defineEmits<{
		(e: 'update:modelValue', value: boolean): void
		(e: 'close'): void
		(e: 'opened'): void
		(e: 'closed'): void
	}>()

	// Local state for uncontrolled / modalId-driven usage. When v-model is bound,
	// modelValue takes precedence; otherwise the registry/internal state drives open/close.
	const localOpen = ref(false)
	const isOpen = computed(() => props.modelValue ?? localOpen.value)

	const setOpen = (value: boolean) => {
		localOpen.value = value
		emit('update:modelValue', value)
	}

	const effectiveCloseOnOverlay = computed(() =>
		props.clickToClose !== undefined ? props.clickToClose : props.closeOnOverlay,
	)
	const effectiveCloseOnEsc = computed(() =>
		props.escToClose !== undefined ? props.escToClose : props.closeOnEsc,
	)

	watch(
		() => props.modalId,
		(id, prev) => {
			if (prev) unregisterModal(prev, setOpen)
			if (id) registerModal(id, setOpen)
		},
		{ immediate: true },
	)

	const isDrawer = computed(() => !!props.drawer)
	const isDrawerLeft = computed(() => props.drawer === 'left')
	const isDrawerRight = computed(() => props.drawer === 'right')
	const isFull = computed(() => props.size === 'full')
	const isFullSize = computed(() => isFull.value || isDrawer.value)

	const drawerWidthClass = computed(() => {
		const widths: Record<ModalSize, string> = {
			xs: 'max-w-xs',
			sm: 'max-w-sm',
			md: 'max-w-md',
			lg: 'max-w-lg',
			xl: 'max-w-2xl',
			full: 'max-w-none',
		}
		return widths[props.size]
	})

	const sizeClasses = computed(() => {
		if (isDrawer.value) return `h-full w-full ${drawerWidthClass.value}`
		const sizes: Record<ModalSize, string> = {
			xs: 'max-w-xs',
			sm: 'max-w-sm',
			md: 'max-w-lg min-h-[200px]',
			lg: 'max-w-2xl min-h-[360px]',
			xl: 'max-w-5xl h-[90vh]',
			full: 'max-w-none w-[calc(100%-1rem)] h-[calc(100%-1rem)] sm:w-[calc(100%-2rem)] sm:h-[calc(100%-2rem)]',
		}
		return sizes[props.size]
	})

	const containerAlignment = computed(() => {
		if (isDrawerLeft.value) return 'items-stretch justify-start'
		if (isDrawerRight.value) return 'items-stretch justify-end'
		if (props.bare) return 'items-center justify-center'
		return 'items-end justify-center sm:items-center'
	})

	const modalLayoutClasses = computed(() => {
		if (isDrawer.value) return 'h-full rounded-none sm:m-2 sm:h-[calc(100%-1rem)] sm:rounded-2xl'
		if (isFull.value) return 'rounded-xl'
		return 'max-h-[90vh] rounded-t-2xl sm:mx-4 sm:rounded-xl w-full'
	})

	const effectiveAnimation = computed<ModalAnimationName>(() => {
		if (isDrawerLeft.value) return 'slide-from-left'
		if (isDrawerRight.value) return 'slide-from-right'
		if (isFull.value) return 'default'
		return props.animation
	})

	const overlayClasses = computed(() =>
		props.overlayStyle === 'dots' ? 'main-modal__overlay--dots' : 'bg-base-300/50 backdrop-blur-sm',
	)

	const animationConfig = computed(() => MODAL_ANIMATIONS[effectiveAnimation.value])

	const contentInitial = computed(() =>
		resolveAnimationState(animationConfig.value.initial, { isFullSize: isFullSize.value }),
	)
	const contentAnimate = computed(() =>
		resolveAnimationState(animationConfig.value.animate, { isFullSize: isFullSize.value }),
	)
	const contentExit = computed(() =>
		resolveAnimationState(animationConfig.value.exit, { isFullSize: isFullSize.value }),
	)
	const contentTransition = computed(() => animationConfig.value.transition)
	const contentOriginClass = computed(() => animationConfig.value.originClass ?? '')

	const overlayTransition = { duration: 0.28, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }

	function close() {
		if (!props.closable) return
		setOpen(false)
		emit('close')
	}

	function handleOverlayClick() {
		if (effectiveCloseOnOverlay.value) {
			close()
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape' && effectiveCloseOnEsc.value && isOpen.value) {
			close()
		}
	}

	function onContentComplete() {
		if (isOpen.value) emit('opened')
	}

	function onExitComplete() {
		emit('closed')
	}

	watch(isOpen, (open) => {
		if (!props.lockScroll) return
		if (open) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
	})

	onMounted(() => {
		document.addEventListener('keydown', handleKeydown)
		if (isOpen.value && props.lockScroll) {
			document.body.style.overflow = 'hidden'
		}
	})

	onUnmounted(() => {
		document.removeEventListener('keydown', handleKeydown)
		document.body.style.overflow = ''
		if (props.modalId) unregisterModal(props.modalId, setOpen)
	})
</script>

<template>
	<Teleport to="body">
		<AnimatePresence @exit-complete="onExitComplete">
			<Motion
				v-if="isOpen"
				as="div"
				:initial="{ opacity: 0 }"
				:animate="{ opacity: 1 }"
				:exit="{ opacity: 0 }"
				:transition="overlayTransition"
				class="fixed inset-0 z-[999] flex [perspective:1600px]"
				:class="containerAlignment"
				v-bind="$attrs">
				<!-- Overlay (visual only — pointer events go through to the wrapper) -->
				<div class="absolute inset-0" :class="overlayClasses" @click="handleOverlayClick" />

				<!-- Modal Content -->
				<Motion
					as="div"
					:initial="contentInitial"
					:animate="contentAnimate"
					:exit="contentExit"
					:transition="contentTransition"
					@animation-complete="onContentComplete"
					@click.stop
					:class="
						bare
							? ['relative z-10', contentOriginClass, customClass]
							: [
									'bg-base-100 ring-primary/35 relative z-10 flex flex-col overflow-hidden shadow-2xl',
									sizeClasses,
									contentOriginClass,
									modalLayoutClasses,
									customClass,
								]
					">
					<template v-if="bare">
						<slot />
					</template>
					<template v-else>
						<!-- Header (if title or showClose) -->
						<header
							v-if="title || (showClose && closable)"
							class="border-base-300 flex shrink-0 items-center justify-between border-b px-3 py-2">
							<h3 v-if="title" class="text-base font-semibold">
								{{ title }}
							</h3>
							<span v-else />
							<button
								v-if="showClose && closable"
								@click="close"
								class="btn btn-ghost btn-circle btn-xs"
								aria-label="Close">
								<X class="h-4 w-4" />
							</button>
						</header>

						<!-- Body -->
						<div class="flex flex-1 flex-col overflow-hidden">
							<slot />
						</div>

						<!-- Footer (optional slot) -->
						<footer v-if="$slots.footer" class="border-base-300 shrink-0 border-t px-4 py-3">
							<slot name="footer" />
						</footer>
					</template>
				</Motion>
			</Motion>
		</AnimatePresence>
	</Teleport>
</template>
